import { createFileRoute } from '@tanstack/react-router';

import { VisitsPage } from '@/app/visits/visits-page';
import type { VisitsTab } from '@/app/visits/visits-tab';

type VisitsSearch = {
  tab: VisitsTab;
  /**
   * Encounter id of the progress-note builder that is currently open. Kept in
   * the URL so a reload (or Back button) restores the builder instead of
   * dropping the user back to the list. Only the opaque id lives here — the
   * visit's display fields are restored from a client-side cache, so no patient
   * PII lands in the address bar.
   */
  documenting?: string;
};

/** Validates the persisted tab selection; anything else falls back to Visits. */
function normalizeVisitsTab(value: unknown): VisitsTab {
  return value === 'progress-notes' ? 'progress-notes' : 'visits';
}

/** Accepts a non-empty encounter id string; anything else drops the param. */
function normalizeDocumenting(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export const Route = createFileRoute('/visits')({
  validateSearch: (search: Record<string, unknown>): VisitsSearch => ({
    tab: normalizeVisitsTab(search['tab']),
    documenting: normalizeDocumenting(search['documenting']),
  }),
  component: VisitsRoute,
});

function VisitsRoute(): React.JSX.Element {
  const { tab, documenting } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <VisitsPage
      tab={tab}
      documenting={documenting}
      onTabChange={(next) => {
        // Preserve any other search params (e.g. an open builder) on tab change.
        void navigate({ search: (prev) => ({ ...prev, tab: next }) });
      }}
      onDocumentingChange={(encounterId) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            documenting: encounterId ?? undefined,
          }),
        });
      }}
    />
  );
}
